import React from "react";

import { Portal, Title, Footer, Header } from "@/components";
import { useCurrentWidth } from "@/hooks";
import { MediaBreakPoints } from "@/resources/constants";

import styles from "./Modal.module.scss";

import { ModalPropsType } from "./types";
import { useNavigate } from "react-router";
import closeIcon from "@/assets/icons/closeIcon.svg";

export const Modal: React.FC<ModalPropsType> = (props) => {
  const {
    children,
    title,
    subTitle,
    isOpen,
    setIsOpen,
    closeFunc,
    closeModal,
    modalWindowWidth = 415,
    mobileContentSize = "full-screen",
    firmBack = false,
    redTitle = false,
    noBorder = false,
  } = props;

  const navigate = useNavigate()
  const windowWidth = useCurrentWidth();
  const breakPoint = MediaBreakPoints.MOBILE_MEDIUM;
  const isFullScreenCondition =
    windowWidth < breakPoint && (mobileContentSize === "full-screen" || mobileContentSize === "full-screen-page-header");
  const isFullScreenPageHeader =
    windowWidth < breakPoint && mobileContentSize === "full-screen-page-header";

  function handleClose() {
    // Сначала вызываем closeModal, если он передан (для модалок, управляемых через URL)
    if (closeModal) {
      closeModal();
    }
    
    // Вызываем closeFunc, если он передан
    if (closeFunc) {
      closeFunc();
    }
    
    // Если передан setIsOpen, используем его для закрытия
    if (setIsOpen) {
      setIsOpen(false);
    }
    
    // Если нет ни setIsOpen, ни closeFunc, ни closeModal, делаем навигацию
    if (!setIsOpen && !closeFunc && !closeModal) {
      navigate('/');
    }
  }

  const closeEsc = React.useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape") {
      handleClose();
    }
  }, [closeModal, closeFunc, setIsOpen, navigate]);

  // Блокируем скролл фонового контента при открытии модалки
  React.useEffect(() => {
    if (isOpen) {
      // Сохраняем текущую позицию скролла
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      // Восстанавливаем скролл при закрытии
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = 'visible';
      if (scrollY) {
        const parsedScrollY = parseInt(scrollY.replace('px', '').replace('-', ''), 10);
        if (!isNaN(parsedScrollY)) {
          window.scrollTo(0, parsedScrollY);
        }
      }
    }

    return () => {
      // Очистка при размонтировании
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = 'visible';
    };
  }, [isOpen]);

  React.useEffect(() => {
    window.addEventListener("keydown", closeEsc);

    return () => {
      window.removeEventListener("keydown", closeEsc);
    };
  }, [closeEsc]);

  const modalCloseButton = renderCloseButton();
  const modalHeader = renderModalHeader();
  const modalBody = renderModalBody();

  function renderCloseButton() {
    return (
      <button className={styles.closeButton} onClick={handleClose}>
        <img src={closeIcon} alt="close" />
      </button>
    );
  }

  function renderModalHeader() {
    const headerType = isFullScreenPageHeader ? "page" : "modal";
    return (
      <Header title={title} headerType={headerType} returnButton={true} onBack={handleClose}/>
    );
  }

  function renderModalBody() {
    const shouldAlwaysShowTitle = mobileContentSize === "fixed";
    const shouldShowTitleInBody = firmBack && isFullScreenCondition;
    const shouldHideTitleInBody = isFullScreenPageHeader; // Для обычного хэдера заголовок уже в хэдере
    
    if (shouldShowTitleInBody) {
      return (
        <div className={styles.body}>
          <div className={styles.bodyContent}>
            {children}
          </div>
        </div>
      );
    }
    
    return (
      <div className={styles.body}>
        {!shouldHideTitleInBody && (shouldAlwaysShowTitle ? (
          <h3 className={redTitle ? styles.body_title_red : styles.body_title}>
            {title}
          </h3>
        ) : (
          <Title newStyles={redTitle ? styles.body_title_red :styles.body_title} mountLocation="modalPage" hType="h3">
            {title}
          </Title>
        ))}
        {(subTitle && (shouldAlwaysShowTitle || !isFullScreenCondition) && !shouldHideTitleInBody) ? (
          <p className={styles.body_subTitle}>{subTitle}</p>
        ) : null}
        {children}
      </div>
    );
  }

  return (
    <>
      {isOpen && (
        <Portal>
          <div className={styles.overlay} onClick={handleClose}>
            <div
              className={`
                ${styles.window}
                ${firmBack ? styles.firmWindow : ''}
                ${mobileContentSize === "fixed" ? styles.windowFixed : ''}
              `}
              style={
                isFullScreenCondition
                  ? { width: "100vw", height: "100vh" }
                  : { width: `${modalWindowWidth}px` }
              }
              onClick={(e) => e.stopPropagation()}
            >
              {isFullScreenCondition ? null : modalCloseButton}
              {isFullScreenCondition ? modalHeader : null}
              {modalBody}
              {isFullScreenCondition && !isFullScreenPageHeader ? <Footer /> : null}
            </div>
          </div>
        </Portal>
      )}
    </>
  );
};
